import { FC, useContext, useRef, useState } from 'react'
import { Box, Flex } from 'components/atoms/Grid'
import styled from 'styled-components'
import { Body2, Body3 } from '@/components/atoms/typography'
import Link from '@/components/atoms/link'
import { HomeBanner, NavigationItem } from '@/network/graphql.g'
import BannersLayout, {
  BannersType
} from '@/components/organisms/bannersLayout'
import useTranslation from 'next-translate/useTranslation'
import { useMainNavigationDispatch } from '@/providers/mainNavigationMenuProvider'
import { Config } from '@/core/config'
import { ContentPaddings } from '@/components/organisms/pageContentWrapper'
import { StoreContext } from '@/providers/storeProvider'
import {
  CampaignNavigationLinks,
  campaignNavigationLinks,
  Gender,
  genderCategoryTestMap,
  GenderNews
} from '@/components/organisms/toolbar/desktop/campaignNavigationLinks'
import { CategoryIconsList } from '@/components/atoms/svg/categoryIcons/categoryIconsList'
import { Translate } from 'next-translate'

type DesktopDropdownMenuRedesignProps = {
  subCategories: NavigationItem[]
  navigationItems: NavigationItem[]
  name?: string
  otherMenuItems: NavigationItem[]
  banners?: HomeBanner[]
  currentMainCategoryId?: string | null
} & Rest

type SlugPath = { id?: string }[]

const makeItems = (
  items: NavigationItem[],
  slugPath: SlugPath,
  setHoveredSubCat: (id: string | null) => void
) =>
  items?.map(({ name, url, categoryId }) => (
    <StyledLi
      key={url}
      onMouseEnter={() => setHoveredSubCat(categoryId)}
      onMouseLeave={() => setHoveredSubCat(null)}
    >
      <StyledItemLink href={url}>
        {CategoryIconsList?.[categoryId] && CategoryIconsList?.[categoryId]}
        <StyledItem
          showIcons={CategoryIconsList?.[categoryId]}
          isActive={slugPath.some((path) => path.id === categoryId)}
          m={0}
        >
          {name}
        </StyledItem>
      </StyledItemLink>
    </StyledLi>
  ))

const makeCampaignItems = (
  items: CampaignNavigationLinks[],
  gender: Gender,
  slugPath: SlugPath,
  t: Translate
) => {
  const filteredItems = items.filter((item) => item.gender === gender)

  return filteredItems.map(({ nameKey, url, campaignId }) => (
    <StyledLi key={url}>
      <StyledItemLink href={url}>
        <StyledItem
          showIcons={false}
          isActive={slugPath.some((path) => path.id === campaignId)}
          m={0}
        >
          {t(nameKey)}
        </StyledItem>
      </StyledItemLink>
    </StyledLi>
  ))
}

const DesktopDropdownMenuRedesign: FC<DesktopDropdownMenuRedesignProps> = ({
  subCategories,
  otherMenuItems,
  banners,
  currentMainCategoryId,
  ...rest
}) => {
  const { t } = useTranslation('common')
  const [openCat, setOpenCat] = useState<string | null>(null)
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null)
  const [lockedHeight, setLockedHeight] = useState<number | null>(null)
  const { slugPath } = useMainNavigationDispatch()
  const [hoveredSubCat, setHoveredSubCat] = useState<string | null>(null)

  const openNow = (id: string) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
    }
    setOpenCat(id)
  }
  const scheduleClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
    }
    closeTimerRef.current = setTimeout(() => {
      setOpenCat(null)
      setLockedHeight(null)
    }, 200)
  }

  const selected = subCategories.find((item) => item.categoryId === openCat)
  const isOpen = !!openCat
  const addCategoryBanners = (
    bannersItems: (HomeBanner & { category?: string })[],
    category: string
  ) => {
    const bannersCategory = [...bannersItems].map((item) => {
      if (!item?.category) {
        return { ...item, category }
      }
      return { ...item }
    })
    return bannersCategory
  }
  const categoryBanners = addCategoryBanners(
    banners || [],
    rest?.name as string
  )
  const { isFrogies } = useContext(StoreContext)

  const genderCategoryMap: Record<Gender, string[]> = {
    men: ['355', '357', '359'],
    women: ['349', '351', '353', '1829'],
    children: ['377', '379', '363', '365']
  }

  const getGenderByCategoryId = (categoryId: string): Gender | null => {
    const genderEntry = Object.entries(genderCategoryMap).find(([ids]) =>
      ids.includes(categoryId)
    )
    return genderEntry ? (genderEntry[0] as Gender) : null
  }

  const gender = openCat ? getGenderByCategoryId(openCat) : null

  const clearCategoryState = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
    }
    setOpenCat(null)
    setHoveredSubCat(null)
    setLockedHeight(null)
  }

  const getNewsUrl = (categoryId?: string | null): string | null => {
    if (!categoryId) {
      return null
    }

    if (categoryId === genderCategoryTestMap.women) {
      return GenderNews.WomenNews
    }

    if (categoryId === genderCategoryTestMap.men) {
      return GenderNews.MenNews
    }

    if (categoryId === genderCategoryTestMap.children) {
      return GenderNews.ChildrenNews
    }

    return null
  }

  const newsUrl = currentMainCategoryId
    ? getNewsUrl(currentMainCategoryId)
    : null

  return (
    <StyledDropdownMenu
      data-testid="desktopDropdownMenu"
      {...rest}
      px={{
        wide: ContentPaddings.wide,
        desktop: ContentPaddings.desktop
      }}
    >
      <Flex
        maxWidth={`${Config.wideScreenPageWidth}px`}
        mx="auto"
        width="100%"
        justifyContent="space-between"
        alignItems="center"
      >
        <SharedHoverArea
          onMouseEnter={() => {
            if (closeTimerRef.current) {
              clearTimeout(closeTimerRef.current)
            }
          }}
          onMouseLeave={scheduleClose}
        >
          <StyledFlex
            flexDirection="row"
            justifyContent="flex-start"
            position="relative"
          >
            {typeof subCategories !== 'undefined' &&
              subCategories
                .filter(({ categoryId }) => !!categoryId)
                .map(({ name, url, categoryId }) => (
                  <SubcategoryBox
                    key={url}
                    data-testid="headerItems"
                    onMouseEnter={() => openNow(categoryId)}
                    onMouseLeave={() => setHoveredSubCat(null)}
                  >
                    <StyledLink href={url}>
                      <SubcategoryHeader
                        isActive={
                          openCat === categoryId || hoveredSubCat === categoryId
                        }
                      >
                        {name}
                      </SubcategoryHeader>
                    </StyledLink>
                  </SubcategoryBox>
                ))}
            {newsUrl && (
              <SubcategoryBox
                onMouseEnter={clearCategoryState}
                onMouseLeave={() => setHoveredSubCat(null)}
              >
                <StyledLink href={newsUrl}>
                  <SubcategoryHeader>
                    {t('Category.Filter.news')}
                  </SubcategoryHeader>
                </StyledLink>
              </SubcategoryBox>
            )}
            <Flex
              alignItems="center"
              position="relative"
              onMouseEnter={clearCategoryState}
            >
              {otherMenuItems?.map(
                ({ name, url, isHighlight, isBigButton }) => (
                  <StyledLink href={url} key={url} data-testid={url}>
                    <SubcategoryHeader flexDirection="row" alignItems="center">
                      <OtherNavigationItem
                        isHighlight={isHighlight}
                        isBigButton={isBigButton}
                      >
                        {name}
                      </OtherNavigationItem>
                    </SubcategoryHeader>
                  </StyledLink>
                )
              )}
            </Flex>
          </StyledFlex>
          <SharedDropdown data-open={isOpen}>
            <DropdownHolder
              style={lockedHeight ? { height: `${lockedHeight}px` } : undefined}
            >
              <DropdownInner
                ref={(element) => {
                  if (!element || !isOpen) {
                    return
                  }
                  const height = element.scrollHeight
                  setLockedHeight((prev) =>
                    typeof prev === 'number' && prev > height ? prev : height
                  )
                }}
              >
                {selected && (
                  <>
                    <ItemsWrapper>
                      <SubcategoryTitle>{selected.name}</SubcategoryTitle>
                      <Items>
                        {makeItems(
                          selected.children,
                          slugPath,
                          setHoveredSubCat
                        )}
                      </Items>
                    </ItemsWrapper>
                    {gender && (
                      <Flex flexDirection="column">
                        <SubcategoryTitle>
                          {t('Campaign.title')}
                        </SubcategoryTitle>
                        <CampaignItems>
                          {makeCampaignItems(
                            campaignNavigationLinks,
                            gender,
                            slugPath,
                            t
                          )}
                        </CampaignItems>
                      </Flex>
                    )}
                    {banners && (
                      <BannerWrapper>
                        <BannersLayout
                          banners={
                            isFrogies
                              ? categoryBanners
                              : categoryBanners?.slice(0, 1)
                          }
                          bannerDst={BannersType.Dropdown}
                        />
                      </BannerWrapper>
                    )}
                  </>
                )}
              </DropdownInner>
            </DropdownHolder>
          </SharedDropdown>
        </SharedHoverArea>
      </Flex>
    </StyledDropdownMenu>
  )
}

export default DesktopDropdownMenuRedesign

const SharedHoverArea = styled.div`
  position: relative;
`

const SharedDropdown = styled(Box)`
  left: 0;
  right: 20px;
  top: 100%;
  position: absolute;
  transform-origin: top;
  transform: scaleY(0);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: transform 360ms ease, opacity 300ms ease,
    visibility 0ms linear 360ms;
  &[data-open='true'] {
    transform: scaleY(1);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition: transform 400ms ease, opacity 400ms ease, visibility 0ms;
  }
`

const DropdownHolder = styled(Box)`
  margin-right: auto;
  min-width: 1140px;
  min-height: 340px;
`

const DropdownInner = styled(Flex)`
  padding: 32px 48px;
  background: #fff;
  gap: 60px;
  justify-content: space-between;
  transition: transform 400ms ease;
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`

const StyledFlex = styled(Flex)`
  padding-right: 20px;
  &::before {
    content: '';
    display: table;
  }
  &::after {
    content: '';
    display: table;
    clear: both;
  }
`

const SubcategoryHeader = styled(Body2)`
  color: ${(props) => props.theme.colors.onBackground};
  margin: 0;
  padding: 15px 12px;
  font-weight: 400;
  position: relative;
  font-size: 14px;
  &::after {
    content: '';
    background: ${(props) => props.theme.colors.onBackground};
    height: 3px;
    position: absolute;
    bottom: 0;
    left: 10px;
    right: ${(props) => (props.isActive ? '15px' : '100%')};
    transition: right 0.3s;
  }
  &:hover {
    color: ${(props) => props.theme.colors.onBackground};
    &::after {
      right: 10px;
    }
  }
`

const StyledDropdownMenu = styled(Flex)`
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  background-color: ${(props) => props.theme.colors.navMenuGrey};
  position: absolute;
  right: 0;
  bottom: 0;
  height: 48px;
  left: 0;
  border-bottom: none;
  margin-top: 0;
`

const StyledLi = styled.li`
  list-style: none;
  margin-top: 2px;
  &:first-child {
    margin-top: 0;
  }
`

const StyledItemLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`

const StyledItem = styled(Body3)`
  color: ${(props) =>
    props.isActive
      ? props.theme.colors.towerColorBlack
      : props.theme.colors.towerColorSecondary};
  line-height: 1.6;
  position: relative;
  transition: color 0.3s;
  white-space: nowrap;
  font-size: 12px;
  font-weight: 400;
  margin-left: ${({ showIcons }) => showIcons && '8px'};
  &::after {
    content: '';
    background-color: ${(props) => props.theme.colors.onBackground};
    border-radius: 2px;
    bottom: 2px;
    content: '';
    height: 2px;
    left: 0;
    opacity: ${(props) => (props.isActive ? '1' : '0')};
    position: absolute;
    right: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    color: ${(props) => props.theme.colors.onBackground};
    cursor: pointer;
    &::after {
      opacity: 1;
    }
  }
`

const StyledLink = styled(Link)`
  display: flex;
  align-items: center;
`

const ItemsDropdown = styled(Box)`
  left: 0;
  right: 20px;
  top: 100%;
  position: absolute;
  transform-origin: top;
  transform: scaleY(0);
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: transform 360ms ease, opacity 300ms ease,
    visibility 0ms linear 360ms;
  &[data-open='true'] {
    transform: scaleY(1);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition: transform 400ms ease, opacity 400ms ease, visibility 0ms;
  }
`

const SubcategoryBox = styled(Box)`
  list-style: none;
  &:hover ${ItemsDropdown} {
    transform: scaleY(1);
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
    transition: transform 400ms ease, opacity 400ms ease, visibility 0ms;
  }
  &:hover ${SubcategoryHeader}:before {
    display: block;
  }
`

const ItemsWrapper = styled(Flex)`
  flex-direction: column;
  flex: 0 1 auto;
`

const SubcategoryTitle = styled(Body2)`
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  margin-top: 0;
  margin-bottom: 16px;
`

const Items = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(8, auto);
  column-gap: 60px;
`

const CampaignItems = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const BannerWrapper = styled(Flex)`
  flex-direction: column;
  padding-top: 3px;
  min-width: 300px;
  max-width: 300px;
`

const OtherNavigationItem = styled.span`
  margin: 0;
  font-weight: ${(props) => (props.isBigButton ? 'bold' : '400')};
  position: relative;
  font-size: 14px;
  color: ${(props) =>
    props.isHighlight
      ? props.theme.colors.navMenuRed
      : props.theme.colors.onBackground};
  text-transform: ${(props) => props.isBigButton && 'uppercase'};
`
